import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtMoney } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { RiskDot } from "@/components/RiskDot";
import { Button } from "@/components/ui/button";
import { CurrencyEur, TrendUp, Warning, ChartBar, CheckCircle, Clock, Paperclip } from "@phosphor-icons/react";

function Card({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon size={20} weight="bold" className={accent} />
      </div>
      <div className="mt-3 font-head text-2xl font-extrabold tracking-tight tabular-nums">{value}</div>
    </div>
  );
}

export default function Finance() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const proofRef = useRef(null);
  const [proofTarget, setProofTarget] = useState(null);

  const load = () => api.get("/finance/summary").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleAdvance = async (e, p) => {
    e.stopPropagation();
    try {
      await api.put(`/finance/projects/${p.id}/advance`, { advance_received: !p.advance_received });
      toast.success(p.advance_received ? "Avans marcat neîncasat" : "Avans confirmat");
      load();
    } catch { toast.error("Eroare la actualizarea avansului"); }
  };

  const markPaid = async (e, p) => {
    e.stopPropagation();
    try {
      const { data: res } = await api.put(`/finance/projects/${p.id}/paid`, { payment_received: !p.payment_received });
      if (!p.payment_received) toast.success(`Încasare confirmată — ${res.notified || 0} departamente notificate`);
      else toast.success("Încasare anulată");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const pickProof = (e, p) => { e.stopPropagation(); setProofTarget(p); proofRef.current?.click(); };
  const onProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !proofTarget) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "dovada");
      fd.append("name", file.name);
      fd.append("department", "financiar");
      await api.post(`/projects/${proofTarget.id}/folder`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dovadă de încasare adăugată în dosarul digital");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { if (proofRef.current) proofRef.current.value = ""; setProofTarget(null); }
  };

  if (!data) return <div className="p-8 text-muted-foreground">Se încarcă…</div>;

  const advancePending = data.projects.filter((p) => !p.advance_received).length;

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="finance-page">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Financiar</p>
        <h1 className="font-head text-3xl font-extrabold tracking-tighter">Buget, costuri & marjă</h1>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card icon={CurrencyEur} label="Buget total" value={fmtMoney(data.total_budget)} accent="text-blue-400" />
        <Card icon={ChartBar} label="Costuri totale" value={fmtMoney(data.total_costs)} accent="text-amber-400" />
        <Card icon={TrendUp} label={`Marjă (${data.total_margin_pct}%)`} value={fmtMoney(data.total_margin)} accent="text-emerald-400" />
        <Card icon={Clock} label="Avansuri neîncasate" value={advancePending} accent={advancePending ? "text-red-400" : "text-muted-foreground"} />
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Proiect</th>
              <th className="px-4 py-3 text-right font-semibold">Buget</th>
              <th className="px-4 py-3 text-right font-semibold">Costuri</th>
              <th className="px-4 py-3 text-right font-semibold">Marjă</th>
              <th className="px-4 py-3 text-left font-semibold">Marjă %</th>
              <th className="px-4 py-3 text-left font-semibold">Avans</th>
              <th className="px-4 py-3 text-left font-semibold">Încasare</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.projects.map((p) => (
              <tr
                key={p.id}
                onClick={() => navigate(`/projects/${p.id}`)}
                className={cn("cursor-pointer transition-colors hover:bg-white/[0.03]", p.alert && "bg-red-500/[0.05]")}
                data-testid={`finance-row-${p.id}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <RiskDot level={p.risk_level} />
                    <div>
                      <div className="font-semibold">{p.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{p.code}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(p.budget)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(p.costs)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(p.margin_value)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-24 overflow-hidden rounded-full bg-white/5">
                      <div className={cn("h-full rounded-full", p.margin_pct < 15 ? "bg-red-500" : p.margin_pct < 30 ? "bg-amber-500" : "bg-emerald-500")}
                        style={{ width: `${Math.max(0, Math.min(100, p.margin_pct))}%` }} />
                    </div>
                    <span className={cn("text-xs font-bold tabular-nums", p.margin_pct < 15 ? "text-red-400" : "text-foreground")}>{p.margin_pct}%</span>
                    {p.alert && <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-bold text-red-400" data-testid={`alert-${p.id}`}>ALERTĂ</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Button
                    variant="ghost" size="sm"
                    onClick={(e) => toggleAdvance(e, p)}
                    className={cn("h-7 gap-1.5 px-2 text-xs", p.advance_received ? "text-emerald-400" : "text-amber-400")}
                    data-testid={`advance-toggle-${p.id}`}
                  >
                    {p.advance_received ? <CheckCircle size={15} weight="fill" /> : <Clock size={15} />}
                    {p.advance_received ? "Încasat" : "Neîncasat"}
                  </Button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="ghost" size="sm"
                      onClick={(e) => markPaid(e, p)}
                      className={cn("h-7 gap-1.5 px-2 text-xs", p.payment_received ? "text-emerald-400" : "text-sky-400")}
                      data-testid={`paid-toggle-${p.id}`}
                    >
                      {p.payment_received ? <CheckCircle size={15} weight="fill" /> : <CurrencyEur size={15} />}
                      {p.payment_received ? "S-a încasat" : "Marchează încasat"}
                    </Button>
                    <button
                      onClick={(e) => pickProof(e, p)}
                      className="rounded-md p-1 text-muted-foreground hover:bg-white/5 hover:text-foreground"
                      title="Adaugă dovadă încasare"
                      data-testid={`proof-btn-${p.id}`}
                    >
                      <Paperclip size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {data.projects.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Niciun proiect.</td></tr>}
          </tbody>
        </table>
      </div>
      <input ref={proofRef} type="file" className="hidden" onChange={onProof} data-testid="finance-proof-input" />
      <p className="mt-3 text-xs text-muted-foreground">
        Alertă automată când marja unui proiect scade sub {data.threshold}%. Avansul neîncasat generează reminder la 24h și escaladare la Director la 48h.
      </p>
    </div>
  );
}
